import {
  Component,
  input,
  output,
  inject,
  OnInit,
  signal,
  computed,
  ViewChild,
  OnChanges,
  effect,
  untracked,
  DestroyRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup
} from '@angular/forms';
import { DialogModule, Dialog } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextarea } from 'primeng/inputtextarea';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Fieldset } from 'primeng/fieldset';

import { LoadingOverlayComponent } from 'src/app/presentation/shared/loading-overlay.component';
import { BuscadorContactosContainer } from 'src/app/presentation/shared/buscadores/container/buscador-contactos.container';
import { DatosProgramacionComponent } from '../datos-programacion/datos-programacion.component';
import { DatosCumplimientoComponent } from '../datos-cumplimiento/datos-cumplimiento.component';
import {
  SeccionTransporteComponent,
  MunicipioPrecargado
} from '../seccion-transporte/seccion-transporte.component';
import { SeccionRiesgoComponent } from '../seccion-riesgo/seccion-riesgo.component';
import { SeccionObservacionesComponent } from '../seccion-observaciones/seccion-observaciones.component';
import { SeccionRecomendacionesComponent } from '../seccion-recomendaciones/seccion-recomendaciones.component';
import { SeccionNotificacionComponent } from '../seccion-notificacion/seccion-notificacion.component';
import {
  SeccionEnviarInformeComponent,
  ArchivoInforme
} from '../seccion-enviar-informe/seccion-enviar-informe.component';
import {
  ContactosAdicionalesDialogComponent,
  ContactoAdicional
} from '../contactos-adicionales-dialog/contactos-adicionales-dialog.component';
import {
  SeccionActaComponent,
  DatosActaFormValue
} from '../seccion-acta/seccion-acta.component';
import {
  ProgramacionPendiente,
  CumplimientoRequest,
  DatosActaRequest
} from 'src/app/domain/model/cumplimiento/cumplimiento';
import {
  LOCALE_NUMERICO,
  MAX_DECIMALES_CANTIDAD,
  YES_VALUE,
  TIPO_CITA, MAX_OBSERVACIONES_JUSTIFICACION_TARDIA
} from 'src/app/common/constants-ejb';
import { DecimalComaDirective } from 'src/app/common/directives/decimal-coma.directive';
import { DecimalHintComponent } from 'src/app/presentation/shared/decimal-hint/decimal-hint.component';
import { InputHoraComponent } from 'src/app/presentation/shared/input-hora/input-hora.component';
import { CumplimientoFormFacade } from '../../services/cumplimiento-form.facade';
import { JustificacionTardiaRechazoStateService } from 'src/app/infrastructure/state/justificacion-tardia-rechazo-state.service';
import { calcularDiasHabiles } from 'src/app/common/date-utils';
import { sanitizeNombreContacto, sanitizeTextarea } from 'src/app/common/input-utils';
import { ContadorCaracteresDirective } from 'src/app/presentation/shared/contador-caracteres/contador-caracteres.directive';

interface DropdownOption {
  label: string;
  value: string | number;
}

/**
 * DUMB COMPONENT: FormularioCumplimiento
 * Dialog para registrar un cumplimiento.
 * Legacy: CreateOrUpdateCumplimientoViewImpl.java
 */
@Component({
  selector: 'app-formulario-cumplimiento',
  standalone: true,
  imports: [
    ContadorCaracteresDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    ToastModule,
    DropdownModule,
    InputTextModule,
    InputNumberModule,
    DatePickerModule,
    DecimalComaDirective,
    DecimalHintComponent,
    InputHoraComponent,
    InputTextarea,
    TooltipModule,
    LoadingOverlayComponent,
    BuscadorContactosContainer,
    Fieldset,
    DatosProgramacionComponent,
    DatosCumplimientoComponent,
    SeccionTransporteComponent,
    SeccionRiesgoComponent,
    SeccionObservacionesComponent,
    SeccionRecomendacionesComponent,
    SeccionNotificacionComponent,
    SeccionEnviarInformeComponent,
    ContactosAdicionalesDialogComponent,
    SeccionActaComponent,
    ConfirmDialogModule
  ],
  providers: [MessageService, ConfirmationService, CumplimientoFormFacade],
  template: `
    <p-toast></p-toast>
    <p-confirmDialog
      [style]="{ width: '92vw', maxWidth: '32rem' }"
    ></p-confirmDialog>
    <p-dialog
      #dlg
      [header]="soloActa() ? 'Asociar Acta' : 'Registrar Cumplimiento'"
      [(visible)]="dialogVisible"
      [modal]="true"
      [style]="{ width: '92vw' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '90vw', '768px': '94vw', '640px': '96vw' }"
      [maximizable]="true"
      appendTo="body"
      [closable]="false"
      [closeOnEscape]="false"
      [dismissableMask]="false"
      (onShow)="onDialogShow()"
    >
      <div
        class="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-end sm:justify-start gap-2 sm:gap-3 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700 w-full"
      >
        <p-button
          label="Regresar"
          icon="pi pi-arrow-left"
          [outlined]="true"
          severity="secondary"
          size="small"
          styleClass="w-full sm:w-auto min-h-[44px]"
          (onClick)="onCancelar()"
        ></p-button>
        @if (!soloActa()) {
          <p-button
            label="Historial de Cumplimientos"
            icon="pi pi-history"
            [outlined]="true"
            severity="secondary"
            size="small"
            styleClass="w-full sm:w-auto min-h-[44px]"
            (onClick)="verHistorial.emit()"
          ></p-button>
        }
        <!-- Con mantenerGuardar (Sin orientación de servicio) el botón de descarga convive con
             Grabar y queda deshabilitado mientras no haya acta guardada. En los demás flujos
             aparece solo tras el guardado exitoso y reemplaza a Grabar. -->
        @if (mantenerGuardar() || mostrarBotonDescargarActa()) {
          <p-button
            label="Descargar Acta Asociada"
            icon="pi pi-download"
            [outlined]="mantenerGuardar()"
            severity="success"
            size="small"
            styleClass="w-full sm:w-auto min-h-[44px]"
            [loading]="descargandoActa()"
            [disabled]="!mostrarBotonDescargarActa()"
            (onClick)="onDescargarActa()"
          ></p-button>
        }
        @if (mantenerGuardar() || !mostrarBotonDescargarActa()) {
        @if (soloActa()) {
          <p-button
            label="Asociar Acta"
            icon="pi pi-link"
            severity="success"
            size="small"
            styleClass="w-full sm:w-auto min-h-[44px]"
            [loading]="actaGenerando()"
            (onClick)="onReintentarClick()"
          ></p-button>
        } @else if (!mostrarBotonReintentar()) {
          <p-button
            label="Grabar"
            icon="pi pi-check"
            severity="success"
            size="small"
            styleClass="w-full sm:w-auto min-h-[44px]"
            [disabled]="loading()"
            (onClick)="onGuardar()"
          ></p-button>
        } @else {
          <p-button
            label="Generar Nuevamente Acta"
            icon="pi pi-refresh"
            severity="warn"
            size="small"
            styleClass="w-full sm:w-auto min-h-[44px]"
            [loading]="actaGenerando()"
            (onClick)="onReintentarClick()"
          ></p-button>
        }
        }
      </div>
      @if (actaError()) {
        <div
          class="flex items-start gap-2 mb-3 p-3 rounded border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-sm"
        >
          <i class="pi pi-exclamation-triangle mt-0.5"></i>
          <span>{{ actaError() }}</span>
        </div>
      }
      @if (programacion()) {
        <form [formGroup]="form">
          <app-loading-overlay [blocked]="loading()" />
          @if (isNoProgramada()) {
            <p-fieldset
              legend="Datos Programacion"
              [toggleable]="true"
              styleClass="mb-4"
            >
              <div class="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3 text-sm">
                @if (!ocultarCamposSinOcSinServicio()) {
                  <div>
                    <span class="font-medium text-gray-600">Orientacion:</span>
                    {{ programacion()!.nmconsecutivoManualOrden || '-' }}
                  </div>
                }
                <div>
                  <span class="font-medium text-gray-600">Cliente:</span>
                  {{ programacion()!.dsnombreCliente || '-' }}
                </div>
                <div>
                  <span class="font-medium text-gray-600">Aseguradora:</span>
                  {{ programacion()!.dsnombreAseguradora || '-' }}
                </div>
                <div>
                  <span class="font-medium text-gray-600">Servicio:</span>
                  {{ programacion()!.dsnombreServicio || '-' }}
                </div>
                <div>
                  <span class="font-medium text-gray-600">Tarea:</span>
                  {{ programacion()!.dstarea || '-' }}
                </div>
                @if (!ocultarCamposSinOcSinServicio()) {
                  <div>
                    <span class="font-medium text-gray-600">Cant. Pedida:</span>
                    {{ programacion()!.nmcantidadProgramada ?? '-' }}
                  </div>
                  <div>
                    <span class="font-medium text-gray-600"
                      >Saldo por Ejecutar:</span
                    >
                    {{ saldoActual() }}
                  </div>
                }
              </div>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Tipo de cita <span class="text-red-500">*</span></label
                  >
                  <p-dropdown
                    formControlName="cdtipoCita"
                    [options]="tiposCita()"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Seleccione tipo de cita"
                    styleClass="w-full"
                    [filter]="true"
                    appendTo="body"
                    (onChange)="onTipoCitaChange($event.value)"
                  ></p-dropdown>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Fecha de Programacion
                    <span class="text-red-500">*</span></label
                  >
                  <p-datepicker
                    formControlName="fechaProgramacion"
                    dateFormat="dd/mm/yy"
                    [showIcon]="true"
                    styleClass="w-full"
                    appendTo="body"
                    [showTransitionOptions]="'0ms'"
                    [hideTransitionOptions]="'0ms'"
                    class="w-full"
                  ></p-datepicker>
                </div>
                @if (mostrarHoraCita()) {
                  <div>
                    <label
                      for="horaCita"
                      class="block text-sm font-medium text-gray-700 mb-1"
                      >Hora cita <span class="text-red-500">*</span></label
                    >
                    <app-input-hora
                      inputId="horaCita"
                      formControlName="horaCita"
                    />
                  </div>
                }
                @if (mostrarDuracionNoProgramada()) {
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                      >Duracion <span class="text-red-500">*</span></label
                    >
                    <p-inputNumber
                      appDecimalComa
                      formControlName="duracion"
                      [locale]="localeNumerico"
                      [min]="0"
                      [minFractionDigits]="0"
                      [maxFractionDigits]="maxDecimalesCantidad"
                      [useGrouping]="false"
                      ariaDescribedBy="ayuda-duracion"
                      placeholder="0,00"
                      inputStyleClass="w-full"
                    />
                    <app-decimal-hint id="ayuda-duracion" ejemplo="0,25" />
                  </div>
                }
              </div>
            </p-fieldset>
          }
          @if (!isNoProgramada()) {
            <app-datos-programacion
              [programacion]="programacion()"
              [ocultarCamposComplementarios]="isCumpComplementarios()"
            ></app-datos-programacion>
          }
          @if (mostrarBloques() && !soloActa()) {
            <app-datos-cumplimiento
              [form]="form"
              [saldoActual]="saldoActual()"
              [esTipoCampo]="esTipoCampoActual()"
              [esTipoInforme]="esTipoInformeActual()"
              [isCumpComplementarios]="isCumpComplementarios()"
              [mostrarLupaContacto]="isNoProgramada()"
              [mostrarNumeroAsistentes]="mostrarAsistentes()"
              [asistentesRequeridos]="asistentesRequeridos()"
              [readonlyNumeroAsistentes]="esActaAsesoria()"
              (abrirContactosDialog)="mostrarContactosDialog.set(true)"
              (abrirBuscadorContactos)="onAbrirBuscadorContactos()"
              (atencionVirtualChange)="onAtencionVirtualChange($event)"
            >
            </app-datos-cumplimiento>
          }
          @if (
            mostrarBloques() &&
            !soloActa() &&
            !esAtencionVirtual() &&
            !esTipoInformeActual() &&
            (isNoProgramada() || esTipoCampoActual())
          ) {
            <app-seccion-transporte
              #seccionTransporte
              [formGroup]="form"
              [municipioOrigenInicial]="municipioOrigenLocal()"
              [municipioEntregaInicial]="municipioEntregaLocal()"
              [nmconsecutivoOficina]="programacion()?.nmconsecutivoOficina"
              [nmconsecutivoCliente]="programacion()?.nmconsecutivoCliente"
              [nmconsecutivoAsesor]="programacion()?.nmconsecutivoAsesor"
              [maxTransporteAdicional]="maxTransporteAdicional()"
              [onObtenerTarifa]="obtenerTarifaCallback"
            >
            </app-seccion-transporte>
          }
          <!--
            HU1132419 — Modo "Asociar Acta": el panel datos-cumplimiento está oculto
            (cumplimiento ya existe) pero el campo "Número de Asistentes" se sigue
            necesitando para el acta. Legacy CreateOrUpdateCumplimientoViewImpl.cargarPaneles
            (rama dto.getNmcumplimiento_servicio() != null): tbNumeroAsistentes permanece
            visible dentro del panelInfoActa, con las mismas reglas (ASESOR read-only +
            auto-count, CAPACITACI editable y requerido).
          -->
          @if (soloActa() && esTipoCampoActual()) {
            <p-fieldset
              legend="Número de Asistentes"
              [toggleable]="true"
              styleClass="mb-4"
            >
              <div
                [formGroup]="form"
                class="grid grid-cols-1 md:grid-cols-4 gap-4"
              >
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Número de Asistentes
                    @if (asistentesRequeridos()) {
                      <span class="text-red-500">*</span>
                    }
                  </label>
                  <p-inputNumber
                    formControlName="numeroAsistentes"
                    [pTooltip]="
                      esActaAsesoria()
                        ? 'Calculado automáticamente a partir de la lista de asistentes'
                        : 'Ingrese el número de asistentes'
                    "
                    [useGrouping]="false"
                    [minFractionDigits]="0"
                    [maxFractionDigits]="0"
                    [min]="1"
                    [readonly]="esActaAsesoria()"
                    inputStyleClass="w-full"
                  >
                  </p-inputNumber>
                  @if (esActaAsesoria()) {
                    <small class="text-gray-500 block mt-1"
                      >Se calcula desde la lista de asistentes del acta.</small
                    >
                  }
                </div>
              </div>
            </p-fieldset>
          }
          <!-- HU1132419: Asociación de Acta (solo tipo CAMPO no exento) -->
          <app-seccion-acta
            [tipoCita]="tipoCitaActual()"
            [dsclasificacion]="programacion()?.dsclasificacion || ''"
            [contratoBeneficiario]="
              programacion()?.cdcontratoBeneficiario || ''
            "
            [contratosExentos]="
              contratosExentos() || contratosExentosActuales()
            "
            (datosActaChange)="onDatosActaChange($event)"
          >
          </app-seccion-acta>
          @if (
            mostrarBloques() &&
            !soloActa() &&
            (!esTipoCampoActual() || isCumpComplementarios()) &&
            !esTipoInformeActual()
          ) {
            <app-seccion-observaciones
              [form]="form"
            ></app-seccion-observaciones>
          }
          @if (!soloActa() && esTipoCampoActual() && !isCumpComplementarios()) {
            <app-seccion-riesgo
              [form]="form"
              (reportarAlertaTemprana)="reportarAlertaTemprana.emit()"
            ></app-seccion-riesgo>
          }
          @if (!soloActa() && esTipoInformeActual()) {
            <app-seccion-enviar-informe
              #seccionEnviarInforme
              [formGroup]="form"
              [ocultarTipoInforme]="isCumpComplementarios()"
              (archivoSeleccionado)="onArchivoInformeSeleccionado($event)"
            >
            </app-seccion-enviar-informe>
          }
          @if (
            !soloActa() && (esTipoInformeActual() || isCumpComplementarios())
          ) {
            <app-seccion-recomendaciones
              [form]="form"
              [obligatorio]="isCumpComplementarios() && !esTipoInformeActual()"
            ></app-seccion-recomendaciones>
          }
          @if (mostrarSeccionNotificacion()) {
            <app-seccion-notificacion [form]="form"></app-seccion-notificacion>
          }
        </form>
      }
    </p-dialog>

    <p-dialog
      [visible]="dialogJustificacionTardiaVisible()"
      header="Ingreso de cumplimiento tardío"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '34rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      appendTo="body"
      (visibleChange)="dialogJustificacionTardiaVisible.set($event)"
      (onHide)="cerrarDialogoJustificacionTardia()"
    >
      <div class="flex flex-col gap-4">
        <div
          class="p-3 bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 dark:border-yellow-600 text-yellow-800 dark:text-yellow-200 rounded"
        >
          <p class="text-sm">
            La programación supera el límite de días hábiles permitido. Debe
            registrar la justificación para continuar.
          </p>
        </div>

        <div class="flex flex-col gap-1">
          <label class="font-medium text-sm"
            >Tipo Justificación <span class="text-red-500">*</span></label
          >
          <p-dropdown
            [options]="justificaciones()"
            [(ngModel)]="cdJustificacionTardiaDialog"
            optionLabel="label"
            optionValue="value"
            placeholder="Seleccione una justificación"
            [showClear]="true"
            [filter]="true"
            filterBy="label"
            styleClass="w-full"
            appendTo="body"
          ></p-dropdown>
        </div>

        <div class="flex flex-col gap-1">
          <label class="font-medium text-sm"
            >Observaciones <span class="text-red-500">*</span></label
          >
          <textarea
            pInputTextarea
            rows="4"
            class="w-full"
            [appContadorCaracteres]="maxObservacionesJustificacionTardia"
            [(ngModel)]="observacionJustificacionTardiaDialog"
            (change)="onCambioObservacionJustificacionDialog()"
            placeholder="Ingrese las observaciones de la justificación"
          ></textarea>
        </div>

        @if (mensajeErrorJustificacionTardia()) {
          <div
            class="p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded text-sm"
          >
            {{ mensajeErrorJustificacionTardia() }}
          </div>
        }
      </div>

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cancelar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="cerrarDialogoJustificacionTardia()"
          ></p-button>
          <p-button
            label="Ingresar"
            icon="pi pi-check"
            styleClass="min-h-[44px]"
            (onClick)="confirmarDialogoJustificacionTardia()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>

    <app-contactos-adicionales-dialog
      [visible]="mostrarContactosDialog()"
      [contactos]="contactosAdicionales()"
      (visibleChange)="mostrarContactosDialog.set($event)"
      (guardar)="onGuardarContactos($event)"
    >
    </app-contactos-adicionales-dialog>
    <app-buscador-contactos
      #buscadorContactos
      (contactoSeleccionado)="onContactoSeleccionado($event)"
      (contactoCreado)="onContactoCreado($event)"
    >
    </app-buscador-contactos>
  `
})
export class FormularioCumplimientoComponent implements OnInit, OnChanges {
  readonly maxObservacionesJustificacionTardia = MAX_OBSERVACIONES_JUSTIFICACION_TARDIA;
  readonly localeNumerico = LOCALE_NUMERICO;
  readonly maxDecimalesCantidad = MAX_DECIMALES_CANTIDAD;
  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly formFacade = inject(CumplimientoFormFacade);
  private readonly destroyRef = inject(DestroyRef);

  /** Tope del valor de transporte adicional (legacy MAX_TTE_ADIC_CMPL). */
  readonly maxTransporteAdicional = this.formFacade.maxTransporteAdicional;

  @ViewChild('seccionTransporte')
  seccionTransporte?: SeccionTransporteComponent;
  @ViewChild('seccionEnviarInforme')
  seccionEnviarInforme?: SeccionEnviarInformeComponent;
  @ViewChild('buscadorContactos')
  buscadorContactos?: BuscadorContactosContainer;
  @ViewChild('dlg') dlg?: Dialog;

  visible = input<boolean>(false);
  programacion = input<ProgramacionPendiente | null>(null);
  saldoActual = input<number>(0);
  loading = input<boolean>(false);
  flujo = input<'PROGRAMADA' | 'NO_PROGRAMADA'>('PROGRAMADA');
  /** Solo para el flujo Sin Orientacion sin servicio. */
  ocultarCamposSinOcSinServicio = input<boolean>(false);
  /**
   * Sin OC de línea de atención (forma de entrega distinta de DOMICILIO y SKYPE_EMPRESARIAL): el correo al
   * contacto lleva una observación. Legacy: isCumpLineaAt → panelNotificacion.
   */
  esLineaAtencion = input<boolean>(false);
  esTipoCampo = input<boolean>(false);
  esTipoInforme = input<boolean>(false);
  requiereJustificacion = input<boolean>(false);
  justificaciones = input<DropdownOption[]>([]);
  tiposCita = input<DropdownOption[]>([]);
  municipioOrigenInicial = input<MunicipioPrecargado | null>(null);
  municipioEntregaInicial = input<MunicipioPrecargado | null>(null);
  /** Callback para obtener tarifa de transporte (evita inyectar PoblacionRepository en dumb component) */
  onObtenerTarifa =
    input<
      (params: {
        cdsuraOrigen: string;
        cdsuraDestino: string;
        cdpoblacion?: string;
        nmconsecutivoOficina?: number;
        nmconsecutivoCliente?: number;
        nmconsecutivoAsesor?: number;
      }) => Promise<number | null>
    >();
  /** Callback para obtener municipio por código DANE (evita inyectar MunicipioRepository en dumb component) */
  onObtenerMunicipio = input<
    (codigo: string) => Promise<{
      cdmunicipio: string;
      cdsura: string;
      dsmunicipio: string;
    } | null>
  >();

  visibleChange = output<boolean>();
  guardar = output<CumplimientoRequest>();
  cancelar = output<void>();
  fechaCumplimientoChange = output<Date>();
  reportarAlertaTemprana = output<void>();
  verHistorial = output<void>();
  reintentar = output<DatosActaRequest>();
  descargarActa = output<void>();

  actaError = input<string | null>(null);
  mostrarBotonReintentar = input<boolean>(false);
  actaGenerando = input<boolean>(false);
  mostrarBotonDescargarActa = input<boolean>(false);
  descargandoActa = input<boolean>(false);
  /** Sin orientación de servicio: Grabar permanece visible tras guardar y "Descargar Acta Asociada"
   *  se muestra aparte (deshabilitado sin acta). En los demás flujos Grabar se oculta al guardar. */
  mantenerGuardar = input<boolean>(false);
  /** Modo "Asociar Acta": el cumplimiento ya existe, solo se ingresan datos del acta para asociarla.
   *  Legacy: ProgramarServiciosViewImpl.asociarActa cuando estado="SIN ASOCIAR ACTA" → reabre form.
   *  Oculta paneles de datos-cumplimiento/transporte/observaciones/riesgo/informe/recomendaciones/justificación
   *  y reusa el flujo de generación de acta (output `reintentar`). */
  soloActa = input<boolean>(false);
  /** Lista de contratos exentos de acta separados por coma (parámetro CONTRATOS_CUMPL).
   *  Legacy: CreateOrUpdateCumplimientoViewImpl.isContratoExentoActa() — el panel acta se oculta
   *  cuando el contrato del beneficiario está en esta lista. */
  contratosExentos = input<string>('');

  dialogVisible = false;
  form!: FormGroup;
  archivoInformeData: ArchivoInforme | null = null;
  private initialDataLoaded = false;
  private cargaConfigTardioPromise: Promise<void> | null = null;

  readonly requiereJustificacionCalculada = signal(false);
  /**
   * El backend rechazó el guardado por falta de justificación aunque el frontend calculó que
   * no era tardío (p. ej. festivos o límite cambiaron entre la carga y el guardado). Mientras
   * el formulario siga abierto, la justificación es obligatoria y debe viajar en el request.
   */
  readonly justificacionExigidaPorBackend = signal(false);
  readonly requiereJustificacionEfectiva = computed(
    () =>
      this.requiereJustificacion() ||
      this.requiereJustificacionCalculada() ||
      this.justificacionExigidaPorBackend()
  );
  private readonly rechazoJustificacion = inject(
    JustificacionTardiaRechazoStateService
  );
  /** Último rechazo atendido: una instancia nueva no debe reaccionar a rechazos anteriores. */
  private ultimaSolicitudRechazoAtendida = this.rechazoJustificacion.solicitud();
  readonly dialogJustificacionTardiaVisible = signal(false);
  readonly mensajeErrorJustificacionTardia = signal<string | null>(null);

  cdJustificacionTardiaDialog: string | number | null = null;
  observacionJustificacionTardiaDialog = '';

  readonly esAtencionVirtual = signal(false);
  readonly mostrarContactosDialog = signal(false);
  readonly contactosAdicionales = signal<ContactoAdicional[]>([]);
  /** Contacto elegido o creado en la lupa (legacy: contactDto → programacion.nmconsecutivo_contacto). */
  private readonly contactoSeleccionadoId = signal<number | null>(null);
  readonly tipoCitaValor = signal<any>(null);
  readonly municipioOrigenLocal = signal<MunicipioPrecargado | null>(null);
  readonly municipioEntregaLocal = signal<MunicipioPrecargado | null>(null);
  /** HU1132419: contratos exentos de acta (viene del response del backend al guardar) */
  readonly contratosExentosActuales = signal<string>('');

  /** Código numérico del tipo de cita actual (para seccion-acta) */
  readonly tipoCitaActual = computed<number>(() => {
    if (this.flujo() !== 'NO_PROGRAMADA') {
      return Number(this.programacion()?.nmconsecutivoTipoCita ?? 0);
    }
    return Number(this.tipoCitaValor() ?? 0);
  });

  /**
   * Legacy: panelNotificacion.setVisible(visible && isCumpLineaAt). La notificación va al correo del contacto, que
   * solo se captura con tipo de cita de campo.
   */
  mostrarSeccionNotificacion(): boolean {
    // Método (no computed): esTipoCampoActual() lee el valor del formulario, que no es un signal
    return (
      this.esLineaAtencion() &&
      this.flujo() === 'NO_PROGRAMADA' &&
      this.mostrarBloques() &&
      !this.soloActa() &&
      this.esTipoCampoActual()
    );
  }

  readonly mostrarBloques = computed(() => {
    if (this.flujo() !== 'NO_PROGRAMADA') return true;
    const v = this.tipoCitaValor();
    return (
      v !== null &&
      v !== undefined &&
      String(v).trim() !== '' &&
      String(v) !== '0'
    );
  });

  constructor() {
    this.form = this.formFacade.initForm(this.fb);
    // Legacy: ingresoCumplimientoTardio() marca cdjustificacion/observacion como required
    // al abrir el diálogo de tardío. Aquí refrescamos validadores cuando cambia el flag.
    effect(() => {
      this.requiereJustificacionEfectiva();
      if (this.form) this.refreshValidators();
    });

    // El backend rechazó el cumplimiento por falta de justificación: se exige y se abre el modal.
    effect(() => {
      const solicitud = this.rechazoJustificacion.solicitud();
      if (solicitud === this.ultimaSolicitudRechazoAtendida) return;
      this.ultimaSolicitudRechazoAtendida = solicitud;
      if (!untracked(() => this.visible())) return;
      this.justificacionExigidaPorBackend.set(true);
      untracked(() => this.abrirDialogoJustificacionTardia());
    });

    effect(() => {
      const visible = this.visible();
      this.programacion();
      this.flujo();
      if (!visible) {
        this.requiereJustificacionCalculada.set(false);
        this.justificacionExigidaPorBackend.set(false);
        return;
      }
      void this.sincronizarEstadoCumplimientoTardio();
    });
  }

  readonly obtenerTarifaCallback = async (params: {
    cdsuraOrigen: string;
    cdsuraDestino: string;
    cdpoblacion?: string;
    nmconsecutivoOficina?: number;
    nmconsecutivoCliente?: number;
    nmconsecutivoAsesor?: number;
  }): Promise<number | null> => {
    const callback = this.onObtenerTarifa();
    if (callback) return callback(params);
    // Fallback: usar el facade propio del componente (que inyecta PoblacionRepository)
    return this.formFacade.obtenerTarifaCallback(params);
  };

  ngOnInit(): void {
    this.form.get('cdtipoCita')?.valueChanges.subscribe((v) => {
      this.tipoCitaValor.set(v);
      this.refreshValidators();
    });

    this.form
      .get('fechaCumplimiento')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fecha) => {
        const fechaNormalizada = this.normalizarFecha(fecha);
        if (fechaNormalizada) {
          this.fechaCumplimientoChange.emit(fechaNormalizada);
        }
        this.recalcularCumplimientoTardio();
      });

    this.form
      .get('fechaProgramacion')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.recalcularCumplimientoTardio();
      });
  }

  ngOnChanges(): void {
    this.dialogVisible = this.visible();
    const prog = this.programacion();
    if (prog && this.form && this.visible() && !this.initialDataLoaded) {
      this.initialDataLoaded = true;
      if (prog.feprogramacion) {
        try {
          this.form.patchValue({
            fechaProgramacion: new Date(prog.feprogramacion)
          });
        } catch {
          /* ignore */
        }
      }

      // Solo en PROGRAMADA se precarga contacto; en NO_PROGRAMADA debe iniciar vacío.
      if (this.flujo() !== 'NO_PROGRAMADA') {
        const nombreContacto = sanitizeNombreContacto(prog.dsnombreContacto);
        const correoContacto = prog.dsemailContacto || '';
        if (nombreContacto || correoContacto) {
          this.form.patchValue({ nombreContacto, correoContacto });
        }
      }

      if (this.flujo() === 'NO_PROGRAMADA') {
        this.form.patchValue({ nombreContacto: '', correoContacto: '' });
        const origenCodigo =
          prog.cdmunicipioOrigen || (prog as any).cdmunicipiOrigen || '';
        const entregaCodigo =
          prog.cdmunicipioEntrega || (prog as any).cdmunicipiEntrega || '';
        this.form.patchValue({
          cdmunicipioOrigen: origenCodigo,
          cdmunicipioEntrega: entregaCodigo,
          cdpoblacion: prog.cdpoblacion || ''
        });
        this.municipioOrigenLocal.set({
          cdmunicipio: origenCodigo,
          cdsura: (prog as any).cdsuraOrigen || '',
          dsmunicipio: (prog as any).dsmunicipioOrigen || ''
        });
        this.municipioEntregaLocal.set({
          cdmunicipio: entregaCodigo,
          cdsura: (prog as any).cdsuraEntrega || '',
          dsmunicipio: (prog as any).dsmunicipioEntrega || ''
        });
        if (origenCodigo && !(prog as any).dsmunicipioOrigen) {
          const cb = this.onObtenerMunicipio();
          if (cb)
            cb(origenCodigo)
              .then((m) => {
                if (m) this.municipioOrigenLocal.set(m);
              })
              .catch(() => {});
        }
        if (entregaCodigo && !(prog as any).dsmunicipioEntrega) {
          const cb = this.onObtenerMunicipio();
          if (cb)
            cb(entregaCodigo)
              .then((m) => {
                if (m) this.municipioEntregaLocal.set(m);
              })
              .catch(() => {});
        }
      } else {
        // Flujo PROGRAMADA: usar los inputs municipioOrigenInicial y municipioEntregaInicial
        const origenInicial = this.municipioOrigenInicial();
        const entregaInicial = this.municipioEntregaInicial();

        if (origenInicial?.cdmunicipio || origenInicial?.dsmunicipio) {
          this.municipioOrigenLocal.set(origenInicial);
          this.form.patchValue({
            cdmunicipioOrigen: origenInicial.cdmunicipio || ''
          });
        }

        if (entregaInicial?.cdmunicipio || entregaInicial?.dsmunicipio) {
          this.municipioEntregaLocal.set(entregaInicial);
          this.form.patchValue({
            cdmunicipioEntrega: entregaInicial.cdmunicipio || ''
          });
        }
      }
      this.refreshValidators();
      this.tipoCitaValor.set(this.form.get('cdtipoCita')?.value ?? null);
      this.recalcularCumplimientoTardio();
    }
    if (!this.visible()) this.initialDataLoaded = false;
  }

  isNoProgramada(): boolean {
    return this.formFacade.isNoProgramada(this.flujo());
  }
  esTipoCampoActual(): boolean {
    return this.formFacade.esTipoCampoActual(
      this.flujo(),
      this.esTipoCampo(),
      this.form?.get('cdtipoCita')?.value
    );
  }
  esTipoInformeActual(): boolean {
    return this.formFacade.esTipoInformeActual(
      this.flujo(),
      this.esTipoInforme(),
      this.form?.get('cdtipoCita')?.value
    );
  }
  /** Legacy: isCumpComplementarios = nmconsecOrdenServDetalle==null && !isCumplimientoSinOc.
   *  Para complementarios el panel de riesgo se oculta. */
  isCumpComplementarios(): boolean {
    const tipo = this.tipoCitaActual();
    return (
      tipo === TIPO_CITA.CAMPO_COMPLEMENTARIO ||
      tipo === TIPO_CITA.INFORME_COMPLEMENTARIO
    );
  }
  mostrarHoraCita(): boolean {
    return this.formFacade.mostrarHoraCita(this.flujo(), this.programacion());
  }
  mostrarDuracionNoProgramada(): boolean {
    return this.formFacade.mostrarDuracionNoProgramada(
      this.flujo(),
      this.programacion()
    );
  }
  mostrarAsistentes(): boolean {
    return this.formFacade.mostrarAsistentes(
      this.flujo(),
      this.esTipoCampo(),
      this.form?.get('cdtipoCita')?.value
    );
  }
  asistentesRequeridos(): boolean {
    return this.formFacade.asistentesRequeridos(
      this.flujo(),
      this.esTipoCampo(),
      this.form?.get('cdtipoCita')?.value,
      this.programacion()?.cdclasificacion || ''
    );
  }

  /**
   * Verifica si el cumplimiento actual corresponde a un Acta de Asesoría.
   * Legacy: CumplimientoFacade.registrarCumplimiento()
   *   isActaAsesoria = !isTipoInforme && !dsClasificacion.startsWith("CAPACITACI")
   * En Asesoría el número de asistentes es read-only y se autocalcula
   * desde la lista de asistentes.
   */
  esActaAsesoria(): boolean {
    const ds = (this.programacion()?.dsclasificacion || '').trim().toUpperCase();
    return ds.length > 0 && !ds.startsWith('CAPACITACI');
  }

  onTipoCitaChange(value: any): void {
    const v = value?.value ?? value;
    this.tipoCitaValor.set(v);
    if (this.form.get('cdtipoCita')?.value !== v)
      this.form.get('cdtipoCita')?.setValue(v);
    this.refreshValidators();
  }

  onAtencionVirtualChange(value: string): void {
    this.esAtencionVirtual.set(value === YES_VALUE);
    this.refreshValidators();
  }

  onGuardarContactos(contactos: ContactoAdicional[]): void {
    this.contactosAdicionales.set(contactos);
    this.mostrarContactosDialog.set(false);
  }

  onArchivoInformeSeleccionado(archivo: ArchivoInforme | null): void {
    this.archivoInformeData = archivo;
  }

  /** HU1132419: Recibe el valor emitido por seccion-acta y lo propaga al facade */
  onDatosActaChange(datos: DatosActaFormValue | null): void {
    this.formFacade.datosActaActual = datos;
    // Legacy: configurarCamposActaServicios() — en Asesoría el campo
    // "Número de Asistentes" es read-only y refleja la cantidad de asistentes de la lista.
    if (datos?.tipoActa === 'ASESOR') {
      const count = datos.asistentes?.length ?? 0;
      const ctrl = this.form?.get('numeroAsistentes');
      if (ctrl && ctrl.value !== count) {
        ctrl.setValue(count > 0 ? count : null, { emitEvent: false });
      }
    }
  }

  onAbrirBuscadorContactos(): void {
    const prog: any = this.programacion();
    if (!prog) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Buscar Contacto',
        detail:
          'No existe beneficiario/centro de trabajo para buscar contactos',
        life: 4000
      });
      return;
    }
    this.buscadorContactos?.abrir({
      cdpersona: prog.cdpersona != null ? Number(prog.cdpersona) : undefined,
      nmconsecCentroTrabajo:
        (prog.nmconsecBenefCt ?? prog.nmconsecCentroTrabajo) != null
          ? Number(prog.nmconsecBenefCt ?? prog.nmconsecCentroTrabajo)
          : undefined,
      nmconsecOrden:
        prog.nmconsecOrdenServDetalle != null
          ? Number(prog.nmconsecOrdenServDetalle)
          : undefined
    });
  }

  onContactoSeleccionado(contacto: any): void {
    if (!contacto) return;
    this.contactoSeleccionadoId.set(contacto.nmconsecutivo ?? null);
    this.form.patchValue({
      nombreContacto: sanitizeNombreContacto(contacto.dsnombre || contacto.nombre),
      correoContacto: contacto.dsemail || contacto.email || ''
    });
  }

  onContactoCreado(contacto: any): void {
    if (!contacto) return;
    this.contactoSeleccionadoId.set(contacto.nmconsecutivo ?? null);
    this.form.patchValue({
      nombreContacto: sanitizeNombreContacto(contacto.dsnombre || contacto.nombre),
      correoContacto: contacto.dsemail || contacto.email || ''
    });
    // Legacy: el contacto creado/seleccionado solo se asigna como contacto principal
    // (nmconsecutivo_contacto); no forma parte de los "contactos adicionales" del cumplimiento.
  }

  async onGuardar(): Promise<void> {
    if (this.loading()) return;

    await this.asegurarConfiguracionTardio();
    if (this.formFacade.configTardioError()) {
      // Reintento único: sin límite de días ni festivos reales no se puede decidir la tardanza
      // (el backend sí la valida y rechazaría el cumplimiento sin justificación).
      await this.asegurarConfiguracionTardio();
      if (this.formFacade.configTardioError()) {
        return; // el facade ya informó el error
      }
    }
    this.recalcularCumplimientoTardio();

    const prog: any = this.programacion();
    const requiereJustificacion = this.requiereJustificacionEfectiva();
    const tieneJustificacionCompleta = this.tieneJustificacionCompleta();

    const errores = this.formFacade.validarFormulario({
      form: this.form,
      esTipoInformeActual: this.esTipoInformeActual(),
      esTipoCampoActual: this.esTipoCampoActual(),
      isNoProgramada: this.isNoProgramada(),
      asistentesRequeridos: this.asistentesRequeridos(),
      requiereJustificacion:
        requiereJustificacion && tieneJustificacionCompleta,
      mostrarHoraCita: this.mostrarHoraCita(),
      mostrarDuracionNoProgramada: this.mostrarDuracionNoProgramada(),
      archivoInformeData: this.archivoInformeData,
      isCumpComplementarios: this.isCumpComplementarios(),
      datosActa: this.formFacade.datosActaActual,
      feprogramacion: prog?.feprogramacion ?? null,
      cantidadProgramada: prog?.nmcantidadProgramada ?? null,
      saldoPorEjecutar: prog?.saldoPorEjecutar ?? null,
      esSinOc: !!prog?.cumplimientoSinOc
    });
    if (errores.length > 0) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Campos obligatorios',
        detail: `Por favor ingrese correctamente los siguientes campos:\n${errores.join('\n')}`,
        life: 5000
      });
      return;
    }

    if (requiereJustificacion && !tieneJustificacionCompleta) {
      this.abrirDialogoJustificacionTardia();
      return;
    }

    if (!prog) return;
    const request = this.formFacade.buildCumplimientoRequest({
      form: this.form,
      prog,
      esTipoInformeActual: this.esTipoInformeActual(),
      esTipoCampoActual: this.esTipoCampoActual(),
      isNoProgramada: this.isNoProgramada(),
      esAtencionVirtual: this.esAtencionVirtual(),
      isCumpComplementarios: this.isCumpComplementarios(),
      requiereJustificacion,
      contactosAdicionales: this.contactosAdicionales(),
      archivoInformeData: this.archivoInformeData,
      incluirObservacionNotificacion: this.mostrarSeccionNotificacion()
    });
    // Legacy onClickGrabar (ViewImpl:2181-2185 y :509): en el cumplimiento sin programación de tipo
    // campo (lupa visible y correo obligatorio), la programación queda con el contacto seleccionado o,
    // si no hay, con el de la orden que tenga el correo digitado (creándolo si no existe).
    // Sin OC la orden aún no existe: legacy trabaja con el DTO de la orden (beneficiario y centro de trabajo
    // que dejó SP_CREAR_BENEF_CT), así que el contacto se resuelve por persona y centro de trabajo.
    const tieneOrden = !!prog.nmconsecOrdenServDetalle;
    const tienePersonaYCentro = !!prog.cdpersona && !!prog.nmconsecBenefCt;
    if (
      this.isNoProgramada() &&
      this.esTipoCampoActual() &&
      !this.isCumpComplementarios() &&
      (tieneOrden || tienePersonaYCentro) &&
      this.form.get('correoContacto')?.value
    ) {
      const nombreTecleado = this.form.get('nombreContacto')?.value;
      const correoTecleado = this.form.get('correoContacto')?.value;
      const nmconsecutivoContacto =
        this.contactoSeleccionadoId() ??
        (tieneOrden
          ? await this.formFacade.resolverContactoProgramacion(
              Number(prog.nmconsecOrdenServDetalle),
              nombreTecleado,
              correoTecleado
            )
          : await this.formFacade.resolverContactoProgramacionPorCentroTrabajo(
              Number(prog.cdpersona),
              Number(prog.nmconsecBenefCt),
              nombreTecleado,
              correoTecleado
            ));
      if (nmconsecutivoContacto) {
        request.nmconsecutivoContacto = nmconsecutivoContacto;
      }
    }

    const mensajeConfirmacion = this.obtenerMensajeConfirmacionGuardado(prog);
    // Legacy onClickGrabar: tras pasar todas las validaciones, mostrar confirmación
    // msgvalidaradvertencia. Aplica para todos los casos (con o sin justificación).
    this.confirmationService.confirm({
      message: mensajeConfirmacion,
      header: 'Confirmar',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      accept: () => this.guardar.emit(request)
    });
  }

  private obtenerMensajeConfirmacionGuardado(programacion: any): string {
    const estadoFactura = String(
      programacion?.estadoFactura ?? ''
    ).toUpperCase();
    const esCalculada =
      estadoFactura.includes('CALCULADA') || estadoFactura.includes('CALC');

    if (esCalculada) {
      return (
        'La factura asociada está en estado CALCULADA. Al grabar el cumplimiento la factura pasará a estado PENDIENTE para recálculo. ' +
        '¿Desea continuar con el guardado?'
      );
    }

    return '¿Está seguro que desea grabar el cumplimiento con los datos ingresados? Los cumplimientos grabados serán visualizados por el cliente en Servicios en Línea y por lo tanto no será posible anularlos';
  }

  onDescargarActa(): void {
    this.descargarActa.emit();
  }

  onCancelar(): void {
    if (this.soloActa()) {
      // En modo soloActa, solo emitir cancelar sin cerrar el diálogo
      // El container/facade maneja la vuelta al historial
      this.cancelar.emit();
      return;
    }
    this.formFacade.resetFormValues(this.form);
    this.esAtencionVirtual.set(false);
    this.contactosAdicionales.set([]);
    this.contactoSeleccionadoId.set(null);
    this.archivoInformeData = null;
    this.initialDataLoaded = false;
    this.tipoCitaValor.set(null);
    this.requiereJustificacionCalculada.set(false);
    this.justificacionExigidaPorBackend.set(false);
    this.cerrarDialogoJustificacionTardia();
    this.dialogVisible = false;
    this.visibleChange.emit(false);
    this.cancelar.emit();
    this.seccionTransporte?.resetMunicipios();
  }

  /** Forzar dialog maximizado al abrirse (la única forma de salir es "Regresar"). */
  onDialogShow(): void {
    const d = this.dlg as unknown as {
      maximized?: boolean;
      maximize?: (e?: Event) => void;
    };
    if (d?.maximize) {
      try {
        d.maximize(new Event('click'));
      } catch {
        /* noop */
      }
    }
    if (d && d.maximized !== true) {
      d.maximized = true;
    }
  }

  /**
   * Valida los campos del acta y asistentes antes de reintentar la generación.
   * Legacy: onClickGenerar360() → validarCamposActa() + validaciones de asistentes de onClickGrabar()
   * HU1132419 — CreateOrUpdateCumplimientoViewImpl.java
   */
  onReintentarClick(): void {
    // Evita múltiples emisiones por doble clic mientras el reintento está en curso.
    if (this.actaGenerando()) {
      return;
    }

    const errores: string[] = [];

    // Validar número de asistentes si el campo está visible
    // Legacy: onClickGrabar → tbNumeroAsistentes check (se aplica también en reintento por solicitud).
    // HU1132419 soloActa: el campo sigue visible dentro del panel de acta y mantiene la misma regla.
    if (this.mostrarAsistentes()) {
      const numAsistentes = this.form.get('numeroAsistentes')?.value;
      const numStr =
        numAsistentes !== null && numAsistentes !== undefined
          ? String(numAsistentes)
          : '';
      if (!numStr.trim()) {
        errores.push(
          'La cantidad en el número de asistentes debe ser mayor a cero (0)'
        );
      } else if (Number(numAsistentes) <= 0) {
        errores.push(
          'La cantidad en el número de asistentes debe ser mayor a cero (0)'
        );
      } else if (numStr.length > 5) {
        errores.push(
          'La cantidad de dígitos permitida para el número de asistentes es hasta 5'
        );
      }
    }

    // Validar campos del acta (Legacy: validarCamposActa())
    const datosActa = this.formFacade.datosActaActual;
    if (datosActa != null) {
      if (!datosActa.descripcionServicioPrestado?.trim()) {
        errores.push(
          'El campo descripción del servicio prestado es obligatoria.'
        );
      }
      const tieneAnexos = (datosActa.anexos?.length ?? 0) > 0;
      const tieneRelacion = !!datosActa.relacionDocumentacion?.trim();

      // Capacitación: anexos obligatorios.
      if (datosActa.tipoActa === 'CAPACITACI' && !tieneAnexos) {
        errores.push('Debe adjuntar de 1 a 4 archivos anexos.');
      }
      // Asesoría: regla bidireccional anexos ↔ relación de documentación.
      if (datosActa.tipoActa === 'ASESOR') {
        if (tieneAnexos && !tieneRelacion) {
          errores.push(
            'Si adjunta anexos, debe diligenciar el campo relación de documentación.'
          );
        }
        if (tieneRelacion && !tieneAnexos) {
          errores.push(
            'Si diligencia Relación de Documentación, debe adjuntar al menos un anexo.'
          );
        }
      }
      if (!datosActa.envioNotificacion) {
        errores.push(
          'Debe seleccionar si desea enviar notificación al contacto.'
        );
      }
    }

    if (errores.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Campos obligatorios del acta',
        detail: errores.join('\n'),
        life: 8000
      });
      return;
    }

    // Construir el payload del acta listo para enviar al backend.
    // El padre solo invoca el endpoint y maneja la respuesta.
    if (!datosActa) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'No hay datos del acta para asociar.',
        life: 5000
      });
      return;
    }
    const numAsistentesValue = this.form.get('numeroAsistentes')?.value;
    const numAsistentes =
      numAsistentesValue !== null &&
      numAsistentesValue !== undefined &&
      numAsistentesValue !== ''
        ? Number(numAsistentesValue)
        : null;
    const payload = this.formFacade.buildDatosActaRequest(
      datosActa,
      undefined,
      numAsistentes
    );
    this.reintentar.emit(payload);
  }

  private refreshValidators(): void {
    this.formFacade.actualizarValidadores({
      form: this.form,
      esTipoInformeActual: this.esTipoInformeActual(),
      esTipoCampoActual: this.esTipoCampoActual(),
      isNoProgramada: this.isNoProgramada(),
      requiereJustificacion: this.requiereJustificacionEfectiva(),
      mostrarHoraCita: this.mostrarHoraCita(),
      mostrarDuracionNoProgramada: this.mostrarDuracionNoProgramada(),
      dstipomanejo: this.programacion()?.dstipomanejo || '',
      isCumpComplementarios: this.isCumpComplementarios()
    });
  }

  private async asegurarConfiguracionTardio(): Promise<void> {
    if (!this.visible()) {
      return;
    }

    if (!this.cargaConfigTardioPromise) {
      this.cargaConfigTardioPromise = this.formFacade
        .cargarConfiguracionTardio()
        .finally(() => {
          this.recalcularCumplimientoTardio();
        });
    }

    await this.cargaConfigTardioPromise;

    // Una carga fallida no se cachea: el siguiente intento vuelve a pedir la configuración.
    if (this.formFacade.configTardioError()) {
      this.cargaConfigTardioPromise = null;
    }
  }

  private async sincronizarEstadoCumplimientoTardio(): Promise<void> {
    await this.asegurarConfiguracionTardio();
    this.recalcularCumplimientoTardio();
  }

  private normalizarFecha(valor: unknown): Date | null {
    if (!valor) {
      return null;
    }

    const fecha = valor instanceof Date ? valor : new Date(String(valor));
    if (Number.isNaN(fecha.getTime())) {
      return null;
    }

    return new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
  }

  private obtenerFechaProgramacionBase(): Date | null {
    const desdeFormulario = this.normalizarFecha(
      this.form.get('fechaProgramacion')?.value
    );
    if (desdeFormulario) {
      return desdeFormulario;
    }

    return this.normalizarFecha(this.programacion()?.feprogramacion);
  }

  private recalcularCumplimientoTardio(): void {
    const fechaProgramacion = this.obtenerFechaProgramacionBase();
    const fechaCumplimiento = this.normalizarFecha(
      this.form.get('fechaCumplimiento')?.value
    );

    if (!fechaProgramacion || !fechaCumplimiento) {
      this.requiereJustificacionCalculada.set(false);
      return;
    }

    const diasHabiles = calcularDiasHabiles(
      fechaProgramacion,
      fechaCumplimiento,
      this.formFacade.festivos()
    );
    const esTardio = diasHabiles > this.formFacade.limitDaysProgram();
    this.requiereJustificacionCalculada.set(esTardio);

    // Si el backend exigió la justificación, no se borra la que el usuario acaba de capturar
    // aunque el cálculo local diga que no es tardío (se reabriría el modal en bucle).
    if (
      !esTardio &&
      !this.requiereJustificacion() &&
      !this.justificacionExigidaPorBackend()
    ) {
      this.form.patchValue(
        {
          cdjustificacion: null,
          observacionesJustificacion: ''
        },
        { emitEvent: false }
      );
    }
  }

  private tieneJustificacionCompleta(): boolean {
    const cdjustificacion = this.form.get('cdjustificacion')?.value;
    const observacion =
      this.form.get('observacionesJustificacion')?.value ?? '';
    return !!cdjustificacion && String(observacion).trim().length > 0;
  }

  private abrirDialogoJustificacionTardia(): void {
    this.cdJustificacionTardiaDialog =
      this.form.get('cdjustificacion')?.value ?? null;
    this.observacionJustificacionTardiaDialog =
      this.form.get('observacionesJustificacion')?.value ?? '';
    this.mensajeErrorJustificacionTardia.set(null);
    this.dialogJustificacionTardiaVisible.set(true);
  }

  cerrarDialogoJustificacionTardia(): void {
    this.dialogJustificacionTardiaVisible.set(false);
    this.mensajeErrorJustificacionTardia.set(null);
  }

  onCambioObservacionJustificacionDialog(): void {
    this.observacionJustificacionTardiaDialog = sanitizeTextarea(
      this.observacionJustificacionTardiaDialog
    );
  }

  confirmarDialogoJustificacionTardia(): void {
    if (!this.cdJustificacionTardiaDialog) {
      this.mensajeErrorJustificacionTardia.set(
        'Debe seleccionar una justificación.'
      );
      return;
    }

    const observacion = sanitizeTextarea(
      this.observacionJustificacionTardiaDialog
    ).trim();
    if (!observacion) {
      this.mensajeErrorJustificacionTardia.set(
        'Debe ingresar las observaciones de la justificación.'
      );
      return;
    }

    const cdjustificacion = Number(this.cdJustificacionTardiaDialog);
    if (Number.isNaN(cdjustificacion)) {
      this.mensajeErrorJustificacionTardia.set(
        'La justificación seleccionada no es válida.'
      );
      return;
    }

    this.form.patchValue({
      cdjustificacion,
      observacionesJustificacion: observacion
    });

    this.cerrarDialogoJustificacionTardia();
    void this.onGuardar();
  }
}
